// SCR-STAFF-888 — مغایرت‌گیری بانکی (US-ACC-051؛ AP-ACC-03): ورود صورت‌حساب بانک از فایل CSV، تطبیق خودکار با ردیف‌های دفتر همان حساب
// بانکی (پنجره تاریخ و مبلغ یکسان)، و برای ردیف نامنطبق «نیازمند سند» با یک ثبت دستی اصلاحی. تسویه درگاه پرداخت بازتطبیق نمی‌شود، چون
// قاعده R-BIL-19 ردیف بدهکار بانک را خودش ساخته و همان ردیف تطبیق می‌خورد (C-AGENT-1771).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AccBankStatementLineView,
  type AccBankStatementSummary,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  KeyValueList,
  ListPage,
  LoadingState,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  accKeys,
  Amount,
  BankLineStatusChip,
  fileToBase64,
  num,
  SCR_STAFF_888,
} from './common';

export function BankReconciliationPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const canReconcile = access.accessMode === 'Full' && access.can('acc.bank.reconcile');
  const [bankAccountId, setBankAccountId] = useState('');
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  // دیالوگ حل ردیف در سطح صفحه نگه داشته می‌شود، نه درون دیالوگ صورت‌حساب: فرم تودرتو رویداد Submit خود را
  // از درخت React به فرم بیرونی می‌رساند و آن دیالوگ را می‌بندد (دام Portal در React).
  const [resolving, setResolving] = useState<AccBankStatementLineView | null>(null);
  const banks = useQuery({
    queryKey: accKeys.banks(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/bank-accounts')),
  });
  const statements = useQuery({
    queryKey: accKeys.statements(session?.tenantId, bankAccountId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/bank-statements', {
          params: { query: { bankAccountId: bankAccountId || undefined } },
        }),
      ),
  });
  const bankTitle = (id: string) =>
    banks.data?.items.find((bank) => bank.id === id)?.title ?? id.slice(0, 8);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['accounting', session?.tenantId] });
  const columns: DataTableColumn<AccBankStatementSummary>[] = [
    {
      id: 'bank',
      headerKey: 'acc:statement.bank_account',
      cell: (row) => (
        <Button size="small" onClick={() => setSelected(row.id)} data-testid="acc-statement-open">
          {bankTitle(row.bankAccountId)}
        </Button>
      ),
    },
    {
      id: 'range',
      headerKey: 'acc:statement.range',
      cell: (row) => (
        <span>
          <JalaliDate value={row.fromDate} /> — <JalaliDate value={row.toDate} />
        </span>
      ),
    },
    {
      id: 'imported',
      headerKey: 'acc:statement.imported_at',
      cell: (row) => <JalaliDate value={row.importedAt} withTime />,
    },
    { id: 'lines', headerKey: 'acc:statement.lines', cell: (row) => faDigits(num(row.lineCount)) },
    {
      id: 'matched',
      headerKey: 'acc:statement.matched',
      cell: (row) => (
        <span data-testid="acc-statement-matched" data-count={num(row.matchedCount)}>
          {faDigits(num(row.matchedCount))}
        </span>
      ),
    },
    {
      id: 'unmatched',
      headerKey: 'acc:statement.unmatched',
      cell: (row) => (
        <span data-testid="acc-statement-unmatched" data-count={num(row.unmatchedCount)}>
          {faDigits(num(row.unmatchedCount))}
        </span>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_888}
      titleKey="acc:statement.title"
      subtitleKey="acc:statement.subtitle"
      actions={
        canReconcile ? (
          <Button variant="contained" onClick={() => setImporting(true)} data-testid="acc-statement-import">
            {t('acc:statement.import')}
          </Button>
        ) : undefined
      }
      filters={
        <TextField
          select
          size="small"
          label={t('acc:statement.bank_account')}
          value={bankAccountId}
          onChange={(event) => setBankAccountId(event.target.value)}
          sx={{ minWidth: 220 }}
          slotProps={{ htmlInput: { 'data-testid': 'acc-statement-bank-filter' } }}
        >
          <MenuItem value="">{t('acc:common.all')}</MenuItem>
          {(banks.data?.items ?? []).map((bank) => (
            <MenuItem key={bank.id} value={bank.id}>
              {bank.title}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      <DataTable
        captionKey="acc:statement.title"
        columns={columns}
        rows={statements.data?.items}
        getRowId={(row) => row.id}
        isLoading={statements.isPending}
        error={statements.error}
        onRetry={() => void statements.refetch()}
        emptyTitleKey="acc:statement.empty"
      />
      {importing ? (
        <ImportDialog
          banks={(banks.data?.items ?? []).map((bank) => ({ id: bank.id, title: bank.title }))}
          onDone={(id) => {
            setImporting(false);
            setSelected(id);
            refresh();
          }}
          onClose={() => setImporting(false)}
        />
      ) : null}
      {selected ? (
        <StatementDetail
          id={selected}
          canReconcile={canReconcile}
          onResolve={setResolving}
          onClose={() => setSelected(null)}
        />
      ) : null}
      {selected && resolving ? (
        <ResolveDialog
          statementId={selected}
          line={resolving}
          onDone={() => {
            setResolving(null);
            refresh();
          }}
          onClose={() => setResolving(null)}
        />
      ) : null}
    </ListPage>
  );
}

function ImportDialog({
  banks,
  onDone,
  onClose,
}: {
  banks: readonly { id: string; title: string }[];
  onDone: (statementId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi } = useTenantSession();
  const key = useIdempotencyKey();
  const [bankAccountId, setBankAccountId] = useState(banks[0]?.id ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [openingBalance, setOpeningBalance] = useState('');
  const { control, watch } = useForm<{ fromDate: string | null; toDate: string | null }>({
    defaultValues: { fromDate: null, toDate: null },
  });
  const upload = useMutation({
    mutationFn: async () => {
      const contentBase64 = file ? await fileToBase64(file) : null;
      return unwrap(
        tenantApi.POST('/api/v1/accounting/bank-statements', {
          body: {
            bankAccountId,
            fromDate: watch('fromDate') ?? '',
            toDate: watch('toDate') ?? '',
            fileName: file?.name ?? null,
            contentBase64,
            lines: null,
            openingBalance: openingBalance
              ? { amount: Number(openingBalance.replace(/\D/g, '')), currency: 'IRR' }
              : null,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      );
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (data) => onDone(data.id),
  });
  return (
    <FormDialog
      open
      titleKey="acc:statement.import"
      maxWidth="sm"
      isDirty={file !== null}
      isSubmitting={upload.isPending}
      submitError={upload.error}
      submitKey="acc:statement.import"
      onSubmit={() => upload.mutate()}
      onClose={onClose}
    >
      <TextField
        select
        label={t('acc:statement.bank_account')}
        value={bankAccountId}
        required
        onChange={(event) => setBankAccountId(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'acc-statement-bank' } }}
      >
        {banks.map((bank) => (
          <MenuItem key={bank.id} value={bank.id}>
            {bank.title}
          </MenuItem>
        ))}
      </TextField>
      <JalaliDatePickerRHF control={control} name="fromDate" labelKey="acc:common.from" />
      <JalaliDatePickerRHF control={control} name="toDate" labelKey="acc:common.to" />
      <TextField
        label={t('acc:statement.opening_balance')}
        value={openingBalance}
        onChange={(event) => setOpeningBalance(event.target.value)}
        slotProps={{
          htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'acc-statement-opening' },
        }}
      />
      <Button component="label" variant="outlined" data-testid="acc-statement-file-button">
        {file ? file.name : t('acc:statement.choose_file')}
        <input
          hidden
          type="file"
          accept=".csv,text/csv"
          data-testid="acc-statement-file"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
      </Button>
      <Typography variant="caption" color="text.secondary">
        {t('acc:statement.file_hint')}
      </Typography>
    </FormDialog>
  );
}

function StatementDetail({
  id,
  canReconcile,
  onResolve,
  onClose,
}: {
  id: string;
  canReconcile: boolean;
  onResolve: (line: AccBankStatementLineView) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const statement = useQuery({
    queryKey: accKeys.statement(session?.tenantId, id),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/accounting/bank-statements/{id}', { params: { path: { id } } })),
  });
  const data = statement.data;
  return (
    <FormDialog
      open
      titleKey="acc:statement.detail_title"
      maxWidth="lg"
      isDirty={false}
      isSubmitting={false}
      submitKey="acc:common.cancel"
      onSubmit={onClose}
      onClose={onClose}
    >
      {statement.isPending ? <LoadingState rows={3} /> : null}
      {statement.error ? (
        <ErrorState error={statement.error} onRetry={() => void statement.refetch()} />
      ) : null}
      {data ? (
        <Stack spacing={2}>
          <KeyValueList
            columns={3}
            items={[
              { labelKey: 'acc:statement.bank_account', value: data.bankAccountTitle ?? '—' },
              { labelKey: 'acc:statement.file', value: <bdi dir="ltr">{data.fileName}</bdi> },
              {
                labelKey: 'acc:statement.range',
                value: (
                  <span>
                    <JalaliDate value={data.fromDate} /> — <JalaliDate value={data.toDate} />
                  </span>
                ),
              },
              { labelKey: 'acc:statement.opening_balance', value: <Amount money={data.openingBalance} /> },
              {
                labelKey: 'acc:statement.closing_balance',
                value: <Amount money={data.closingBalance} testId="acc-statement-closing" />,
              },
              {
                labelKey: 'acc:statement.ledger_balance',
                value: <Amount money={data.ledgerBalance} testId="acc-statement-ledger" />,
              },
              {
                labelKey: 'acc:statement.difference',
                value: <Amount money={data.difference} testId="acc-statement-difference" />,
              },
              { labelKey: 'acc:statement.matched', value: faDigits(num(data.matchedCount)) },
              { labelKey: 'acc:statement.unmatched', value: faDigits(num(data.unmatchedCount)) },
            ]}
          />
          {num(data.difference.amount) === 0 ? (
            <Alert severity="success" data-testid="acc-statement-balanced">
              {t('acc:statement.balanced')}
            </Alert>
          ) : (
            <Alert severity="warning" data-testid="acc-statement-unbalanced">
              {t('acc:statement.unbalanced')}
            </Alert>
          )}
          <Paper variant="outlined">
            <Table size="small" aria-label={t('acc:statement.lines')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('acc:common.level')}</TableCell>
                  <TableCell>{t('acc:common.date')}</TableCell>
                  <TableCell>{t('acc:statement.reference')}</TableCell>
                  <TableCell>{t('acc:journal.description')}</TableCell>
                  <TableCell align="right">{t('acc:common.amount')}</TableCell>
                  <TableCell>{t('acc:common.status')}</TableCell>
                  <TableCell>{t('acc:statement.matched_journal')}</TableCell>
                  <TableCell>{t('acc:common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.lines.map((line) => (
                  <TableRow key={line.id} data-testid="acc-statement-line" data-status={line.status}>
                    <TableCell>{faDigits(num(line.lineNo))}</TableCell>
                    <TableCell>
                      <JalaliDate value={line.date} />
                    </TableCell>
                    <TableCell>
                      <bdi dir="ltr">{line.reference ?? '—'}</bdi>
                    </TableCell>
                    <TableCell>{line.description ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Amount money={line.amount} />
                    </TableCell>
                    <TableCell>
                      <BankLineStatusChip status={line.status} />
                    </TableCell>
                    <TableCell>
                      <bdi dir="ltr">{line.matchedJournalNumber ?? '—'}</bdi>
                    </TableCell>
                    <TableCell>
                      {canReconcile && (line.status === 'Unmatched' || line.status === 'NeedsEntry') ? (
                        <Button
                          size="small"
                          onClick={() => onResolve(line)}
                          data-testid="acc-statement-resolve"
                        >
                          {t('acc:statement.resolve')}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      ) : null}
    </FormDialog>
  );
}

/** ردیف نامنطبق با یک سند اصلاحی قطعی (کارمزد، سود، برداشت ناشناخته) حل می‌شود (AC-ACC-051.1). */
function ResolveDialog({
  statementId,
  line,
  onDone,
  onClose,
}: {
  statementId: string;
  line: AccBankStatementLineView;
  onDone: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const [accountId, setAccountId] = useState('');
  const [detail1Id, setDetail1Id] = useState('');
  const [description, setDescription] = useState(line.description ?? '');
  const accounts = useQuery({
    queryKey: accKeys.accounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/accounts')),
  });
  const details = useQuery({
    queryKey: accKeys.details(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/detail-accounts')),
  });
  const leafAccounts = (accounts.data?.items ?? []).filter(
    (account) =>
      account.isActive && !(accounts.data?.items ?? []).some((child) => child.parentId === account.id),
  );
  const resolve = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/bank-statements/{id}/lines/{lineId}:resolve', {
          params: { path: { id: statementId, lineId: line.id } },
          body: {
            accountId,
            detail1Id: detail1Id || null,
            description: description.trim() || null,
          },
        }),
      ),
    onSuccess: onDone,
  });
  return (
    <FormDialog
      open
      titleKey="acc:statement.resolve_title"
      maxWidth="sm"
      isDirty={accountId !== ''}
      isSubmitting={resolve.isPending}
      submitError={resolve.error}
      submitKey="acc:statement.resolve"
      onSubmit={() => resolve.mutate()}
      onClose={onClose}
    >
      <KeyValueList
        columns={2}
        items={[
          { labelKey: 'acc:common.date', value: <JalaliDate value={line.date} /> },
          { labelKey: 'acc:common.amount', value: <Amount money={line.amount} /> },
          {
            labelKey: 'acc:statement.suggested_role',
            value: line.suggestedRole ?? t('acc:common.none'),
          },
        ]}
      />
      <TextField
        select
        label={t('acc:journal.account')}
        value={accountId}
        required
        onChange={(event) => setAccountId(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'acc-resolve-account' } }}
      >
        <MenuItem value="">{t('acc:common.none')}</MenuItem>
        {leafAccounts.map((account) => (
          <MenuItem key={account.id} value={account.id}>
            <bdi dir="ltr">{account.code}</bdi>&nbsp;{account.title}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label={t('acc:journal.details')}
        value={detail1Id}
        onChange={(event) => setDetail1Id(event.target.value)}
      >
        <MenuItem value="">{t('acc:common.none')}</MenuItem>
        {(details.data?.items ?? [])
          .filter((detail) => detail.isActive)
          .map((detail) => (
            <MenuItem key={detail.id} value={detail.id}>
              <bdi dir="ltr">{detail.code}</bdi>&nbsp;{detail.title}
            </MenuItem>
          ))}
      </TextField>
      <TextField
        label={t('acc:journal.description')}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'acc-resolve-description' } }}
      />
    </FormDialog>
  );
}
